import {
  useDeviceOrientationContext,
  MotionFadeInFromTop,
  useBooleanState,
  Iconify,
} from '@mfw/components';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import { Outlet } from 'react-router-dom';
import Box from '@mui/material/Box';
import React from 'react';

import { RouteContentErrorBoundary } from '../../InlineRouteErrorPage';
import { useNavigationContext, useAuthContext } from '../../../context';
import ClientOnboardingDialog from '../../OnboardingPages/ClientOnboarding/ClientOnboardingDialog';
import { DailyActivityModal } from '../../../allFeatures/DailyActivity';
import { PostExitOfferDialog } from '../../../allFeatures/OrganizationExit';
import { MembershipCardSetupHost } from '../../../allFeatures/Payments/MembershipCardSetupHost';
import { AccountCardsRequestHost } from '../../../allFeatures/AccountBilling/AccountCardsRequestHost';
import { MembershipTouAcceptanceDialog } from '../../../allFeatures/Payments/MembershipTouAcceptanceDialog';
import { ClientMembershipAlertBanner } from '../../../allFeatures/Payments/ClientMembershipAlertBanner';
import { BillingStatusBanner } from '../../../allFeatures/Payments/BillingStatusBanner';
import { usePostExitOfferStore } from '../../../store/usePostExitOfferStore';
import useFetchInvitation from '../../../hooks/invitation/useFetchInvitation';
import { useNavigate, usePathname } from '../../../router/hooks';
import InvitationModal from '../../../components/Invitation';
import AppHeader from '../../../allFeatures/AppHeader';
import { Sidebar } from '../../../allFeatures';
import { useAppSockets } from '../../../hooks';
import { Spinner } from '../../../components';
import { useOverlayBackClose } from '../../../components/DialogBrowserBack';
import { useInitialLoadGate } from './useInitialLoadGate';

const SIDEBAR_WIDTH = 280;
const MINI_SIDEBAR_WIDTH = 80;

const AppLayout = () => {
  const { currentUser } = useAuthContext();
  const { data: invitation } = useFetchInvitation();
  const { isPostExitOfferOpen, setPostExitOfferOpen } = usePostExitOfferStore();
  const navigate = useNavigate();
  useAppSockets();

  const showClientOnboarding = useBooleanState();

  const pathname = usePathname();
  // A `member` mid-invitation is org-less by design and must not get to its
  // personal profile from here (Settings → Account settings) — so, like the
  // sidebar below, the header/billing banner are suppressed on this page too.
  const isPendingInvitation = pathname === '/pending-invitation';
  const {
    isMiniBarAvailable,
    isMobile,
    isNavigationOpen,
    setMiniBarAvailable,
    setNavigationOpen,
  } = useNavigationContext();

  const navigationBackClose = useOverlayBackClose<HTMLDivElement>(
    isMobile && isNavigationOpen,
    () => setNavigationOpen(false),
  );

  const { isFixedAppLayoutContent } = useDeviceOrientationContext();
  const { isInitialLoad, isOrganizationLoading } = useInitialLoadGate();

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        if (!isMobile) setMiniBarAvailable((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, setMiniBarAvailable]);

  const sidebarWidth = !isMobile
    ? isMiniBarAvailable
      ? MINI_SIDEBAR_WIDTH
      : SIDEBAR_WIDTH
    : 0;

  // Shared with the width calc below — the Main box must reserve exactly the
  // width the Sidebar box occupies, and only when it's actually in the flex
  // flow (mobile renders it `fixed`, out of flow, so it never reserves space).
  const isSidebarEligible =
    Boolean(currentUser) &&
    !pathname.startsWith('/auth/onboarding') &&
    !isPendingInvitation &&
    Boolean(currentUser?.hasFinishedOnboarding);

  return (
    <Box display="flex" height="100%" width="100%">
      {/* MV2-596 — one loader over the whole shell (sidebar, header, page and
          any dialog) until the first screen after sign-in is complete. The
          page keeps mounting underneath so its queries are what we wait on. */}
      {isInitialLoad && (
        <Box
          zIndex="tooltip"
          bgcolor="background.default"
          justifyContent="center"
          data-testid="app-initial-loading"
          alignItems="center"
          position="fixed"
          display="flex"
          bottom={0}
          right={0}
          left={0}
          top={0}
        >
          <Spinner size="large" />
        </Box>
      )}
      {invitation && (
        <InvitationModal
          onClientOnboardingRequired={showClientOnboarding.on}
          invitation={invitation}
          open={!!invitation}
        />
      )}
      <Dialog
        PaperProps={{ sx: { maxHeight: '90vh' } }}
        open={showClientOnboarding.isActive}
        maxWidth="md"
        fullWidth
      >
        <ClientOnboardingDialog
          onFinish={() => {
            showClientOnboarding.off();
            navigate('/');
          }}
        />
      </Dialog>

      {/* F9 — post-exit "create a new profile?" offer. Hosted here (not in
          the page that opened the exit wizard) because the wizard navigates
          home on success and unmounts its opener. */}
      <PostExitOfferDialog
        onClose={() => setPostExitOfferOpen(false)}
        open={isPostExitOfferOpen}
      />

      {/* Daily Activity — opened from a reminder notification (any page) or
          the manual "Log Daily Activity" button; hosted here for the same
          reason as the dialog above. */}
      {currentUser && !currentUser.isPlatformUser && <DailyActivityModal />}

      {/* MV2-573 §1 — runs the "Link card & activate" flow triggered from a
          MEMBERSHIP_CARD_SETUP_REQUIRED notification (any page), including
          the Stripe-return resume; hosted here for the same reason. */}
      {currentUser && !currentUser.isPlatformUser && (
        <MembershipCardSetupHost />
      )}

      {/* A failed or suspended card charge notification (any page) opens
          the payer's card list; hosted here for the same reason. */}
      <AccountCardsRequestHost />

      {/* Recurring membership Terms of Use — opened from a
          MEMBERSHIP_TOU_UPDATED notification (any page) or right after
          AssignMembershipDialog assigns a client's first RECURRING
          membership; hosted here for the same reason as the two above. */}
      {currentUser && !currentUser.isPlatformUser && (
        <MembershipTouAcceptanceDialog />
      )}

      {/* Mobile overlay */}
      {isMobile && isNavigationOpen && (
        <Box
          {...navigationBackClose}
          sx={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9,
          }}
          onClick={() => setNavigationOpen(false)}
          position="fixed"
          bottom={0}
          right={0}
          left={0}
          top={0}
        />
      )}

      {/* Sidebar */}
      {isSidebarEligible && (!isMobile || isNavigationOpen) && (
        <Box
          sx={{
            transition: 'width 0.2s ease',
          }}
          position={isMobile ? 'fixed' : 'relative'}
          width={isMobile ? '75%' : sidebarWidth}
          zIndex={isMobile ? 10 : undefined}
          bgcolor="background.default"
          overflow="visible"
          component="aside"
          height="100%"
        >
          <Box position="relative" overflow="hidden" height="100%" zIndex={1}>
            <MotionFadeInFromTop>
              <Sidebar>
                <Sidebar.Header />
                <Sidebar.Content />
              </Sidebar>
            </MotionFadeInFromTop>
          </Box>
          {!isMobile && (
            <IconButton
              sx={{
                '&:hover': { backgroundColor: 'background.paper' },
                backgroundColor: 'background.default',
                border: '1px dashed',
                borderColor: 'grey.700',
                position: 'absolute',
                right: '-14px',
                top: '21px',
                zIndex: 2,
              }}
              onClick={() => setMiniBarAvailable((prev) => !prev)}
              data-testid="sidebar-toggle"
              size="small"
            >
              <Iconify
                icon={
                  isMiniBarAvailable
                    ? 'mdi:arrow-expand-horizontal'
                    : 'mdi:arrow-collapse-horizontal'
                }
                size="small"
              />
            </IconButton>
          )}
        </Box>
      )}

      {/* Main */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transition: 'width 0.2s ease',
          width: '100%',
          ...(isSidebarEligible &&
            !isMobile && {
              width: `calc(100% - ${sidebarWidth}px)`,
            }),
        }}
        component="main"
      >
        {currentUser && !isFixedAppLayoutContent && !isPendingInvitation && (
          <AppHeader />
        )}
        {currentUser && !isFixedAppLayoutContent && !isPendingInvitation && (
          <BillingStatusBanner />
        )}
        {currentUser && !isFixedAppLayoutContent && !isPendingInvitation && (
          <ClientMembershipAlertBanner />
        )}

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: 'auto',
            ...(isFixedAppLayoutContent && {
              inset: 0,
              position: 'fixed',
              // Above the sidebar's collapse-toggle IconButton (zIndex: 2),
              // so immersive/landscape content fully covers the app shell.
              zIndex: 3,
            }),
          }}
        >
          {/*
            A render fault below this point is CONTENT, not chrome. Without a
            boundary here React unwinds to the nearest one, which is the router's
            boundary for the route whose element is this very layout — so a
            crash in any page unmounted the sidebar, the header, the providers
            and every socket. The fallback clears itself when the user
            navigates, so the sidebar it preserves actually leads somewhere.
          */}
          {/* Until the organization is resolved the page would render as the
              personal profile and fire that profile's queries. */}
          {!(isInitialLoad && isOrganizationLoading) && (
            <RouteContentErrorBoundary>
              <Outlet />
            </RouteContentErrorBoundary>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
